import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Clock, Car, Shield, CheckCircle, ArrowRight, Phone, Zap, FileText } from 'lucide-react';
import { submitTemporaryInsuranceForm } from '../data/mock';
import { toast } from 'sonner';

// Import refactored sub-components
import {
  VehicleInfoSection,
  DurationSection,
  DriverInfoSection,
  DocumentsSection,
  EligibilitySection
} from '../components/temporary-insurance';

const initialFormData = {
  vehicleType: '',
  brand: '',
  model: '',
  registration: '',
  duration: '',
  startDate: '',
  startTime: '',
  firstName: '',
  lastName: '',
  birthDate: '',
  address: '',
  licenseNumber: '',
  email: '',
  phone: '',
  details: '',
  licensePhotoFront: null,
  licensePhotoBack: null,
  registrationCardPhoto: null,
  hasMoreThan2YearsLicense: false,
  noAlcoholDrugRetention: false,
  notProfessionalOwner: false,
  notRentalVehicle: false
};

const features = [
  {
    icon: Zap,
    title: 'Couverture immédiate',
    description: 'Protection active dès validation'
  },
  {
    icon: Clock,
    title: 'De 1 à 90 jours',
    description: 'Durée flexible selon vos besoins'
  },
  {
    icon: FileText,
    title: 'Documents par email',
    description: 'Carte verte envoyée instantanément'
  }
];

export const TemporaryInsurance = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileNames, setFileNames] = useState({
    licensePhotoFront: '',
    licensePhotoBack: '',
    registrationCardPhoto: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCheckboxChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.checked });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const name = e.target.name;
    
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier est trop volumineux. Maximum 5 MB.');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Seules les images sont acceptées.');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, [name]: reader.result });
        setFileNames({ ...fileNames, [name]: file.name });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.hasMoreThan2YearsLicense || !formData.noAlcoholDrugRetention || 
        !formData.notProfessionalOwner || !formData.notRentalVehicle) {
      toast.error("Vous devez confirmer toutes les conditions d'éligibilité pour souscrire.");
      return;
    }
    
    if (!formData.licensePhotoFront || !formData.licensePhotoBack || !formData.registrationCardPhoto) {
      toast.error("Veuillez télécharger tous les documents requis (permis recto/verso + carte grise).");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const backendFormData = {
        vehicle_type: formData.vehicleType,
        brand: formData.brand,
        model: formData.model,
        registration: formData.registration,
        duration: formData.duration,
        start_date: formData.startDate,
        start_time: formData.startTime,
        first_name: formData.firstName,
        last_name: formData.lastName,
        birth_date: formData.birthDate,
        address: formData.address,
        license_number: formData.licenseNumber,
        email: formData.email,
        phone: formData.phone,
        details: formData.details,
        license_photo_front: formData.licensePhotoFront,
        license_photo_back: formData.licensePhotoBack,
        registration_card_photo: formData.registrationCardPhoto,
        has_more_than_2_years_license: formData.hasMoreThan2YearsLicense,
        no_alcohol_drug_retention: formData.noAlcoholDrugRetention,
        not_professional_owner: formData.notProfessionalOwner,
        not_rental_vehicle: formData.notRentalVehicle
      };

      const result = await submitTemporaryInsuranceForm(backendFormData);
      if (result.success) {
        toast.success(result.message);
        setFormData(initialFormData);
        setFileNames({ licensePhotoFront: '', licensePhotoBack: '', registrationCardPhoto: '' });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Une erreur est survenue. Veuillez réessayer ou nous appeler au 05 82 81 91 20.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Assurance Temporaire 24h/24 | CAMBC Conseils Moissac 82</title>
        <meta name="description" content="Assurance auto temporaire en ligne 24h/24. De 1 à 90 jours, couverture immédiate. Cabinet CAMBC Conseils, courtier agréé Moissac (82)." />
        <meta name="keywords" content="assurance temporaire, assurance auto temporaire, assurance voiture temporaire, assurance courte durée, assurance en ligne, Moissac, 82200" />
        <link rel="canonical" href="https://www.cambc-conseils.fr/assurance-temporaire" />
      </Helmet>

      <div className="min-h-screen bg-[#F9F8F6]">
        {/* Hero Section */}
        <section className="relative pt-32 pb-16 bg-[#0F2137] overflow-hidden">
          <div className="absolute inset-0 opacity-[0.03]" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C5A059]/20 rounded-full text-[#C5A059] text-sm font-bold mb-6">
                <Clock size={16} />
                <span>Disponible 24h/24 - 7j/7</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                Assurance Auto
                <span className="block text-[#C5A059]">Temporaire</span>
              </h1>
              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
                Souscription immédiate pour vos besoins d'assurance de courte durée.
                De 1 à 90 jours, recevez vos documents par email.
              </p>
              
              {/* Features */}
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                      <feature.icon size={18} className="text-[#C5A059]" />
                    </div>
                    <div className="text-left">
                      <div className="text-white font-medium text-sm">{feature.title}</div>
                      <div className="text-slate-400 text-xs">{feature.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#C5A059] to-transparent"></div>
        </section>

        {/* Form Section */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
              {/* Form Header */}
              <div className="bg-[#0F2137] p-6 sm:p-8">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#C5A059]/20 flex items-center justify-center">
                    <Car size={28} className="text-[#C5A059]" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">Demande d'Assurance Temporaire</h2>
                    <p className="text-slate-400">Remplissez ce formulaire et recevez votre devis sous 2 heures</p>
                  </div>
                </div>
              </div>

              {/* Form Content */}
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Vehicle Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-blue-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Informations Véhicule</h3>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-6">
                      <VehicleInfoSection 
                        formData={formData} 
                        handleChange={handleChange} 
                        handleSelectChange={handleSelectChange} 
                      />
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-[#C5A059] rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Durée et Date de Début</h3>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-6">
                      <DurationSection 
                        formData={formData} 
                        handleChange={handleChange} 
                      />
                    </div>
                  </div>

                  {/* Driver Info */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-emerald-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Informations Conducteur</h3>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-6">
                      <DriverInfoSection 
                        formData={formData} 
                        handleChange={handleChange} 
                      />
                    </div>
                  </div>

                  {/* Documents */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-violet-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Documents Requis</h3>
                    </div>
                    <div className="bg-slate-50 rounded-xl p-6">
                      <DocumentsSection 
                        fileNames={fileNames} 
                        handleFileChange={handleFileChange} 
                      />
                    </div>
                  </div>

                  {/* Eligibility */}
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="w-1 h-6 bg-rose-500 rounded-full"></div>
                      <h3 className="text-lg font-bold text-[#0F2137]">Conditions d'Éligibilité</h3>
                    </div>
                    <div className="bg-rose-50 rounded-xl p-6 border border-rose-100">
                      <EligibilitySection 
                        formData={formData} 
                        handleCheckboxChange={handleCheckboxChange} 
                      />
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="space-y-2">
                    <Label className="text-[#0F2137] font-medium">
                      Informations complémentaires (optionnel)
                    </Label>
                    <Textarea
                      name="details"
                      value={formData.details}
                      onChange={handleChange}
                      placeholder="Précisez toute information utile : usage du véhicule, conducteurs supplémentaires, antécédents..."
                      rows={4}
                      className="border-slate-200 focus:border-[#C5A059] focus:ring-[#C5A059] rounded-xl resize-none"
                    />
                  </div>

                  {/* Info Box */}
                  <div className="p-4 bg-[#F5EFE6] rounded-xl border border-[#C5A059]/20">
                    <p className="text-sm text-[#0F2137]">
                      <strong className="text-[#C5A059]">Service Express 24/7 :</strong> Une fois votre demande envoyée, notre équipe vous contactera sous 2 heures (même en soirée et week-end) pour finaliser votre souscription et vous envoyer vos documents par email.
                    </p>
                  </div>

                  {/* Submit Button */}
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-[#C5A059] hover:bg-[#B08A45] text-white text-lg py-6 rounded-xl font-semibold shadow-lg"
                    disabled={isSubmitting}
                    data-testid="submit-insurance-form"
                  >
                    {isSubmitting ? (
                      'Envoi en cours...'
                    ) : (
                      <>
                        <Clock className="mr-2" size={20} />
                        Envoyer ma demande (Réponse sous 2h)
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid sm:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <Clock size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Disponible 24/7</h3>
                <p className="text-sm text-slate-600">Souscription possible à toute heure, même le week-end</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <Shield size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Couverture Immédiate</h3>
                <p className="text-sm text-slate-600">Protégé dès la validation de votre souscription</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#F5EFE6] flex items-center justify-center">
                  <CheckCircle size={28} className="text-[#C5A059]" />
                </div>
                <h3 className="font-bold text-[#0F2137] mb-2">Simple et Rapide</h3>
                <p className="text-sm text-slate-600">Documents envoyés par email en quelques minutes</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-12 bg-[#0F2137]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Une question ? Besoin d'aide ?
            </h2>
            <p className="text-slate-300 mb-6">
              Notre équipe est disponible pour vous accompagner dans votre souscription
            </p>
            <a href="tel:0582819120">
              <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08A45] text-white px-8 py-6 rounded-xl font-semibold">
                <Phone size={20} className="mr-2" />
                05 82 81 91 20
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};
